import React, {
  createContext,
  useContext,
  useState,
  useLayoutEffect,
} from "react";
import { useMediaQuery, useTheme } from "@mui/material";

const ResponsiveContext = createContext();

// True phone-sized viewport (MUI's "sm" breakpoint, 600px by default),
// independent from the per-screen ResponsiveProvider threshold above
// (which is really a "tablet or narrower" toggle used for the toolbar).
export const useIsMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("sm"));
};

// Mirrors the ResponsiveProvider threshold screens pass to AppBarSearch
// (typically 900px). Screens that decide, ABOVE the AppBarSearch they
// render, whether to force grid view / hide the view-toggle button must use
// this instead of useIsMobile's 600px breakpoint — otherwise there's a
// 600-900px gap where AppBarSearch has already collapsed into its compact
// mobile header but the screen still passes a view-toggle handler, so the
// "single add button" fast path never kicks in and users get the old
// filter-icon-opens-a-drawer flow instead of the direct "+" button.
export const useIsCompactHeader = (maxWidth = 900) => {
  return useMediaQuery(`(max-width:${maxWidth}px)`);
};

export const ResponsiveProvider = ({ children, maxWidth = 900 }) => {
  const [isSmallScreen, setIsSmallScreen] = useState(true);

  useLayoutEffect(() => {
    const checkScreenSize = () => {
      const match = window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
      setIsSmallScreen((prev) => {
        if (prev !== match) return match;
        return prev;
      });
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, [maxWidth]);

  return (
    <ResponsiveContext.Provider value={{ isSmallScreen }}>
      {children}
    </ResponsiveContext.Provider>
  );
};

export const useResponsive = () => {
  const context = useContext(ResponsiveContext);

  if (!context) {
    throw new Error("useResponsive must be used within a ResponsiveProvider");
  }

  return context;
};
